import { message, Modal, Tag } from 'antd';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components/macro';

import { getNameFromType } from '../../../containers/profile/sidebar/Ownership/ownershipUtils';
import analytics, { EventType, EntityActionType } from '../../../../../analytics';
import { useTranslation } from '../../../../../../i18n/useTranslation';
import { useEntityRegistry } from '../../../../../useEntityRegistry';
import { useEntityData } from '../../../EntityContext';
import OwnerContent from './OwnerContent';

import { useRemoveOwnerMutation } from '../../../../../../graphql/mutations.generated';
import { EntityType } from '../../../../../../types.generated';

const OwnerTag = styled(Tag)`
    padding: 2px;
    padding-right: 6px;
    margin-bottom: 8px;
    display: inline-flex;
    align-items: center;
`;

type Props = {
    entityUrn?: string;
    hidePopOver?: boolean | undefined;
    refetch?: () => Promise<any>;
    readOnly?: boolean;
    fontSize?: number;
    owner: any;
};

export const ExpandedOwner = ({ entityUrn, owner, hidePopOver, refetch, readOnly, fontSize }: Props) => {
    const { t } = useTranslation();
    const entityRegistry = useEntityRegistry();
    const { entityType } = useEntityData();
    const [removeOwnerMutation] = useRemoveOwnerMutation();
    let name = '';
    let ownershipTypeName = '';
    if (owner.owner.__typename === 'CorpGroup') {
        name = entityRegistry.getDisplayName(EntityType.CorpGroup, owner.owner);
    }
    if (owner.owner.__typename === 'CorpUser') {
        name = entityRegistry.getDisplayName(EntityType.CorpUser, owner.owner);
    }
    if (name === '__datahub_system') {
        name = 'System';
    }

    if (owner.ownershipType && owner.ownershipType.info) {
        ownershipTypeName = owner.ownershipType.info.name;
    } else if (owner.type) {
        ownershipTypeName = getNameFromType(owner.type);
    }
    const pictureLink =
        (owner.owner.__typename === 'CorpUser' && owner.owner.editableProperties?.pictureLink) || undefined;
    const onDelete = async () => {
        if (!entityUrn) {
            return;
        }
        try {
            await removeOwnerMutation({
                variables: {
                    input: {
                        ownerUrn: owner.owner.urn,
                        ownershipTypeUrn: owner.ownershipType?.urn,
                        resourceUrn: entityUrn,
                    },
                },
            });
            message.success({ content: t('Owner Removed'), duration: 2 });
            analytics.event({
                type: EventType.EntityActionEvent,
                actionType: EntityActionType.UpdateOwnership,
                entityType,
                entityUrn,
            });
        } catch (e: unknown) {
            message.destroy();
            if (e instanceof Error) {
                message.error({
                    content: t('Failed to remove owner: {{message}}', { message: e.message || '' }),
                    duration: 3,
                });
            }
        }
        refetch?.();
    };
    const onClose = (e) => {
        e.preventDefault();
        Modal.confirm({
            title: t('Do you want to remove {{name}}?', { name }),
            content: t('Are you sure you want to remove {{name}} as an {{ownershipTypeName}} type owner?', {
                name,
                ownershipTypeName,
            }),
            onOk() {
                onDelete();
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            className: 'f-borderless-modal',
            okButtonProps: {
                type: undefined,
                danger: true,
            },
            autoFocusButton: null,
            maskClosable: true,
            centered: true,
        });
    };

    // Checks if the user is named System
    const isSystem = () => {
        return name === 'System';
    };

    // This will stop the redirection to User Profile Page if the name is System
    const path = isSystem() ? '#' : `${entityRegistry.getEntityUrl(owner.owner.type, owner.owner.urn)}`;

    return (
        <OwnerTag className="grey-tag" onClose={onClose} closable={!!entityUrn && !readOnly}>
            <Link to={path}>
                <OwnerContent
                    name={name}
                    owner={owner}
                    hidePopOver={hidePopOver}
                    pictureLink={pictureLink}
                    fontSize={fontSize}
                />
            </Link>
        </OwnerTag>
    );
};
